// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ConsentType } from '@imprivata-cloud/authn';
import { checkConsent } from '@api/selfServiceServices';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import {
  DOCUMENT_PURPOSE_BIOMETRIC_CONSENT,
  DOCUMENT_PURPOSE_BIOMETRIC_NOTICE,
  DOCUMENT_SOURCE_CUSTOMER,
  DOCUMENT_SOURCE_IMPRIVATA,
} from './constants';

export const useConsentData = (pickedState?: string) => {
  const { emitError } = useErrorContext();

  // Use React Query to check biometrics consent
  const consentQuery = useQuery({
    queryKey: ['consent-check'],
    queryFn: () => checkConsent({ consentType: ConsentType.BIOMETRICS }),
  });

  useEffect(() => {
    if (consentQuery.isError) {
      emitError(AppError.fromMessage('Error checking consent requirements'));
    }
  }, [consentQuery.isError, emitError]);

  // Get the customer notice document to display
  const customerNoticeDoc = useMemo(() => {
    if (!consentQuery.data) {
      return null;
    }

    return (
      consentQuery.data.documentMetadata?.find(
        (doc) =>
          doc.documentPurpose === DOCUMENT_PURPOSE_BIOMETRIC_NOTICE && doc.documentSource === DOCUMENT_SOURCE_CUSTOMER,
        // Customer Notice Doc doesn't require resident state check
      ) || null
    );
  }, [consentQuery.data]);

  // Check if we should show Imprivata Consent
  const shouldShowImprivataConsent = useMemo(() => {
    if (!consentQuery.data || !pickedState) {
      return false;
    }
    // Find imprivata biometric consent documents
    const imprivataConsents =
      consentQuery.data.documentMetadata?.filter(
        (doc) =>
          doc.documentPurpose === DOCUMENT_PURPOSE_BIOMETRIC_CONSENT &&
          doc.documentSource === DOCUMENT_SOURCE_IMPRIVATA,
      ) || [];
    // Check if any imprivata consent applies to the picked state
    return imprivataConsents.some((consent) =>
      consent.requiredResidenceLocations?.some((location) => location.shortName === pickedState),
    );
  }, [consentQuery.data, pickedState]);

  // Check if we should show Customer Consent
  const shouldShowCustomerConsent = useMemo(() => {
    if (!consentQuery.data || !pickedState) {
      return false;
    }

    // Find customer biometric consent documents
    const customerConsents =
      consentQuery.data.documentMetadata?.filter(
        (doc) =>
          doc.documentPurpose === DOCUMENT_PURPOSE_BIOMETRIC_CONSENT && doc.documentSource === DOCUMENT_SOURCE_CUSTOMER,
      ) || [];

    // Check if any customer consent applies to the picked state
    return customerConsents.some((consent) =>
      consent.requiredResidenceLocations?.some((location) => location.shortName === pickedState),
    );
  }, [consentQuery.data, pickedState]);

  // Get the imprivata consent document to display
  const imprivataConsentDoc = useMemo(() => {
    if (!consentQuery.data || !pickedState || !shouldShowImprivataConsent) {
      return null;
    }

    return (
      consentQuery.data.documentMetadata?.find(
        (doc) =>
          doc.documentPurpose === DOCUMENT_PURPOSE_BIOMETRIC_CONSENT &&
          doc.documentSource === DOCUMENT_SOURCE_IMPRIVATA &&
          doc.requiredResidenceLocations?.some((location) => location.shortName === pickedState),
      ) || null
    );
  }, [consentQuery.data, pickedState, shouldShowImprivataConsent]);

  // Get the customer consent document to display
  const customerConsentDoc = useMemo(() => {
    if (!consentQuery.data || !pickedState || !shouldShowCustomerConsent) {
      return null;
    }

    return (
      consentQuery.data.documentMetadata?.find(
        (doc) =>
          doc.documentPurpose === DOCUMENT_PURPOSE_BIOMETRIC_CONSENT &&
          doc.documentSource === DOCUMENT_SOURCE_CUSTOMER &&
          doc.requiredResidenceLocations?.some((location) => location.shortName === pickedState),
      ) || null
    );
  }, [consentQuery.data, pickedState, shouldShowCustomerConsent]);

  return {
    consentQuery,
    customerNoticeDoc,
    shouldShowImprivataConsent,
    shouldShowCustomerConsent,
    imprivataConsentDoc,
    customerConsentDoc,
  };
};
